import { useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { askConfirm } from '../../../../shared/confirmDialog'
import { downloadFile, getFormattedDate } from '../../../../shared/helpers'
import eventEmitter, { types } from '../../../../shared/eventEmitter'

import Btn from '../../../../UI/Btn'
import Table from '../../../../UI/Table'
import MiniHeader from '../../../../UI/MiniHeader'
import BtnTooltip from '../../../../UI/BtnTooltip'

import graphIcon from '../../../../assets/icons/graph.svg'

import styles from './style.module.scss'

/**
 * Die Anteilseigner:innen der eigenen Junior Company.
 *
 * Die Liste beginnt mit dem Namen der Person (wie jede Liste der Anwendung,
 * siehe UI/Table) — bis 09.08.2026 stand dort der Betrag, und wer jemanden
 * suchte, las in der zweiten Spalte. Ein Klick auf die Zeile öffnet den
 * Datensatz; das Drei-Punkte-Menü behält dieselben Handlungen.
 */
function Shares(props) {
	const { t } = useTranslation()
	const [shares, setShares] = useState([])
	const [countAll, setCountAll] = useState(0)
	const [page, setPage] = useState(0)

	const [sort, setSort] = useState({ name: 'firstName', direction: 'asc' })
	const limit = 10

	const fetchShares = (offset = page * limit) => {
		http.get(TApi.STUDENT_GET_SHARES, {
			params: {
				limit,
				offset,
				order: sort.name,
				direction: sort.direction
			}
		}).then(({ data }) => {
			setShares(data.rows)
			setCountAll(data.count)
		})
	}

	useEffect(() => {
		fetchShares(page * limit)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort])

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	const canEdit = !props.isTeacherStudentView

	const openDetail = shareholder => {
		eventEmitter.emit(types.openShareholderDetail, {
			shareholder,
			downloadUrl: TApi.STUDENT_DOWNOLOAD_SHARE_CONSENT,
			// Die Lehrkraft sieht die Junior Company nur an — löschen darf sie
			// dort nichts.
			deleteUrl: canEdit ? TApi.STUDENT_DELETE_SHARE : undefined,
			refresh: fetchShares
		})
	}

	const downloadConsent = shareholder => {
		http.get(TApi.STUDENT_DOWNOLOAD_SHARE_CONSENT(shareholder.consentDocumentHashName), {
			responseType: 'blob'
		}).then(({ data }) => downloadFile(shareholder.consentDocumentHashName, data))
	}

	// Dieselbe Rückfrage wie im Detailfenster: Was gelöscht wird, steht im Text,
	// nicht nur „Sind Sie sicher?".
	const remove = async shareholder => {
		const confirmed = await askConfirm({
			title: t('Delete shareholder'),
			text: t('The shareholder and the uploaded consent document will be deleted.'),
			confirmLabel: t('Delete'),
			cancelLabel: t('Cancel'),
			tone: 'danger'
		})
		if (!confirmed) return

		http.delete(TApi.STUDENT_DELETE_SHARE(shareholder.id)).then(() => {
			toast.success(t('Shareholder has been removed successfully.'))
			fetchShares()
		})
	}

	const columns = useMemo(
		() => [
			{
				Header: () => <span className={styles.tableTitleName}>Name</span>,
				accessor: 'firstName',
				minWidth: 220,
				Cell: ({ row }) => (
					<span className={styles.name}>
						{row.original.firstName} {row.original.lastName}
					</span>
				)
			},
			{
				Header: t('Company Share'),
				accessor: 'share',
				Cell: ({ row }) => (
					<span className={styles.shareContainer}>
						<img className={styles.shareIcon} src={graphIcon} alt="" />
						<span className={styles.share}>{row.original.companyShare}&nbsp;€</span>
					</span>
				)
			},
			{
				Header: t('Signed on'),
				accessor: 'date',
				Cell: ({ row }) => (
					<div className={styles.dateContainer}>
						<span className={styles.date}>{getFormattedDate(row.original.createdAt)}</span>
						{/* Die Einverständniserklärung bleibt einen Klick weit weg —
						    ein Knopf in der Zeile hat seinen eigenen Klick und
						    öffnet nicht zusätzlich den Datensatz (siehe UI/Table).
						    Der Hinweistext wird von UI/Btn übersetzt, deshalb steht
						    hier der Schlüssel. */}
						<Btn
							theme="download"
							icon="download"
							iconSize={{ w: 24, h: 24 }}
							tooltip="Download consent document"
							onClick={() => downloadConsent(row.original)}
						/>
					</div>
				)
			},
			{
				accessor: 'actions',
				width: 10,
				Cell: ({ row }) => (
					<div className={styles.rowMenu}>
						<BtnTooltip
							items={[
								{ label: t('Show details'), onClick: () => openDetail(row.original) },
								...(canEdit ? [{ label: t('Delete'), onClick: () => remove(row.original) }] : [])
							]}
						/>
					</div>
				)
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[shares, canEdit, t]
	)

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title={t('Company Shares')} bg="#f8faff">
				{canEdit && (
					<Btn
						className={styles.addShareBtn}
						iconClassName={styles.addShareBtnIcon}
						icon="plus"
						iconColor="#fff"
						theme="orange"
						onClick={() => {
							eventEmitter.emit(types.openAddShareholderModal, null, fetchShares)
						}}
					>
						{t('Add Shareholder')}
					</Btn>
				)}
			</MiniHeader>
			<div className={styles.content}>
				{!shares.length ? (
					<div className={styles.empty}>
						<h3 className={styles.emptyTitle}>{t('No shareholders yet')}</h3>
						<p className={styles.emptyText}>{t('No shareholders yet hint')}</p>
					</div>
				) : (
					<Table
						columns={columns}
						items={shares}
						limit={limit}
						countAll={countAll}
						onChange={setPage}
						handleSort={handleSort}
						onRowClick={openDetail}
						rowTitle={t('Show details')}
					/>
				)}
			</div>
		</div>
	)
}

Shares.propTypes = {
	isTeacherStudentView: PropTypes.bool.isRequired
}

const mapStateToProps = state => ({
	isTeacherStudentView: state.user.isTeacherStudentView
})

export default connect(mapStateToProps)(Shares)
